import { useEffect } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { getOffer, withSubId, pick } from '../offers/config';
import { captureAttribution } from '../offers/attribution';

// ============================================================
// /go/:slug  ->  REDIRECCIÓN DE SALIDA AL AFILIADO
// ------------------------------------------------------------
// Por qué existe:
//   1. Los botones apuntan a una ruta propia y legible, no a una URL
//      larga de impact pegada en el HTML.
//   2. Un solo lugar donde vive el link real. Si Kovo cambia el link o
//      entra otra oferta, se toca offers/config.ts y nada más.
//   3. Aquí se disparan los eventos de salida y se construye el subId
//      con los parámetros de la campaña antes de irse.
//
// ------------------------------------------------------------
// POR QUÉ SE DISPARAN DOS EVENTOS  (19/09/2026)
// ------------------------------------------------------------
// Lead          -> evento ESTÁNDAR de Meta. Se reconoce solo, sin
//                  crear una custom conversion, y es contra el que
//                  optimiza la campaña hoy. Es el que permite lanzar.
//
// AffiliateClick -> evento CUSTOM. Es el nombre correcto: un click
//                  hacia Kovo no es un lead de Blinkblis, porque aquí
//                  no se captura correo ni teléfono. Acumula en
//                  paralelo para poder migrar el ad set más adelante
//                  sin haber perdido historial.
//
// Disparar ambos no duplica el gasto ni confunde a Meta: la campaña
// optimiza únicamente contra el evento elegido en el ad set.
//
// ------------------------------------------------------------
// CARRERA CONTRA EL REDIRECT  (bug encontrado el 19/09/2026)
// ------------------------------------------------------------
// fbq() no envía el evento de forma síncrona: lo encola y lo manda por
// una petición de red. Si llamamos window.location.replace() en el
// mismo tick, el navegador abandona la página y CANCELA esa petición.
// El evento nunca llega a Meta.
//
// Síntoma real: AffiliateClick no aparecía en el dataset aunque el
// código lo disparaba. Sin ese evento, la campaña optimiza a ciegas.
//
// Solución: dar una ventana corta (REDIRECT_DELAY_MS) para que las
// peticiones salgan. El usuario no percibe la demora porque esta
// pantalla ya muestra un spinner.
// ============================================================
// Snapshot 19/09/2026 - fuerza entrada de version

const REDIRECT_DELAY_MS = 600;
export function Go() {
  const {
    slug
  } = useParams();
  const {
    search
  } = useLocation();
  const offer = getOffer(slug);
  useEffect(() => {
    if (!offer) return;

    // Asegura que los parámetros de esta URL queden guardados antes de
    // construir el subId (por si entraron directo a /go/...).
    captureAttribution();
    const w = window as any;
    if (typeof w.fbq === 'function') {
      const payload = {
        network: offer.network,
        brand: offer.brand,
        offer: offer.slug,
        content_category: pick(offer.category, 'en')
      };

      // Estándar: es el que la campaña usa hoy para optimizar.
      w.fbq('track', 'Lead', payload);

      // Custom: el nombre correcto, acumulando para el futuro.
      w.fbq('trackCustom', 'AffiliateClick', payload);
    }
    const destino = withSubId(offer.affiliateUrl, offer.slug);

    // replace: no deja esta ruta en el historial, así el botón "atrás"
    // devuelve a la página de oferta y no a un bucle de redirección.
    const t = window.setTimeout(() => {
      window.location.replace(destino);
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [offer, search]);
  if (!offer) return <Navigate to="/" replace data-id="element-393" data-mp="34imyh8za6hvx" />;
  return <div className="min-h-screen bg-background text-white flex flex-col items-center justify-center gap-5 px-6 text-center" data-id="element-394" data-mp="34jobvk3rrggt">
      <div className="w-10 h-10 rounded-full border-2 border-white/15 border-t-accent animate-spin" data-id="element-395" data-mp="js4ajoc5buhx" />
      <p className="text-white/50 text-[15px] font-light" data-id="element-396" data-mp="hdhbixj3ncck">
        {`Redirecting to ${offer.brand}…`}
      </p>
      <a href={withSubId(offer.affiliateUrl, offer.slug)} rel="nofollow sponsored noopener noreferrer" className="text-accent text-[14px] underline hover:no-underline" data-id="element-397" data-mp="dc5kcnkkuj29">
        {`Continue to ${offer.brand}`}
      </a>
    </div>;
}